import { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { ELSDropDownOption } from '@els/els-component-form-field-react';
import { AssessmentRS, ChartFragment, ChartFragmentRS } from 'models/api-response';
import { ELSModalServiceType } from 'models/els';
import { ChartActionsComponentProps, ChartComponentProps, MarDosageRecord, MarRecord, SaveOptions } from 'models/ui';
import { Locales, NAV_ID } from 'constants/app.constant';
import { appHelper } from 'helpers';
import { appActions, appSelectors } from 'redux/ducks/app';
import { studentActions, studentSelectors } from 'redux/ducks/student';
import { Box, ChartActions, ChartPermission } from 'components/common';
import { ELSAccordion, ELSAccordionItem, ELSButton, ELSCheckBox, ELSDropDown, ELSFlex, ELSFlexItem, ELSIcon, ELSTooltip } from 'components/common/els';
import FilterSelect from 'components/common/filter-select/FilterSelect';
import { HOLD_UNHOLD_MODE } from './constants';
import InactiveOrderTooltip from './InactiveOrderTooltip';
import MarAdministerSidebar from './MarAdministerSidebar';
import MarDosageSidebar, { MarSidebarProps } from './MarDosageSidebar';
import MarHoldAndDiscontinueFormSidebar from './MarHoldAndDiscontinueFormSidebar';
import MarHoldUnholdSidebar from './MarHoldUnholdSidebar';
import MarTable from './MarTable';
import {
  buildAdHocHeldDosage,
  buildUnholdFragment,
  expandOrdersByDate,
  getAllAdministerRecords,
  getEarliestOrderStartDate,
  getHoldableDosageTimes,
  isScheduledOrder,
  sortByDosageTime
} from './helper';
import './mar.scss';
import Notice from '../order-entry/shared/Notice';

export interface MarViewProps extends Partial<ChartComponentProps> {
  isAuthor: boolean;
  statSelected: boolean;
  scheduledSelected: boolean;
  prnSelected: boolean;
  continuousIVSelected: boolean;
  inactiveOrderSelected: boolean;
  viewDate: string;
  statRecords: MarRecord[];
  scheduledRecords: MarRecord[];
  prnRecords: MarRecord[];
  continuousIVRecords: MarRecord[];
  inactiveOrderRecords: MarRecord[];
  selectedDosageRecord: MarDosageRecord;
  doseFormMode: string;
  fragments: ChartFragment[];
  locale: Locales;
  presaveFragment: Function;
  onStatChange: Function;
  onScheduledChange: Function;
  onPrnChange: Function;
  onContinuousIVChange: Function;
  onInactiveOrderChange: Function;
  onEditDosageRecord: Function;
  handleHoldMedication: Function;
  handleDiscontinueMedication: Function;
  handleUnHoldMedication: Function;
  onRecordUpdate: Function;
  onSidebarClose: Function;
  saveChartData: (payload: object) => Promise<ChartFragmentRS>;
  saveChart?: <T>(fragments: Array<Partial<ChartFragment>>, options?: SaveOptions) => Promise<T>;
  chartActionsProps: ChartActionsComponentProps;
  viewDateOptions: ELSDropDownOption[];
  onSelectViewDate: Function;
  selectedNavId?: string;
  assessment?: AssessmentRS;
  isShowEHRMar?: boolean;
  modalService?: ELSModalServiceType;
  setChartingTimeMAR?: Function;
  chartingTimeMAR?: string;
}

const STAT_LABEL = 'Stat';
const SCHEDULED_LABEL = 'Scheduled';
const PRN_LABEL = 'PRN';
const INACTIVE_ORDERS_LABEL = 'Inactive Orders';
const CONTINUOUS_IV_LABEL = 'Continuous IV';

const MAR_FILTER_OPTIONS = [STAT_LABEL.toUpperCase(), SCHEDULED_LABEL, PRN_LABEL, CONTINUOUS_IV_LABEL, INACTIVE_ORDERS_LABEL] as const;
const MAR_FILTER_DATE = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
export type MarFilterItem = typeof MAR_FILTER_OPTIONS[number];
export type MarFilterDayItem = typeof MAR_FILTER_DATE[number];

// eslint-disable-next-line sonarjs/cognitive-complexity
const MarView = (props: MarViewProps) => {
  const history = useHistory();
  const marFilterOptions: MarFilterItem[] = [...MAR_FILTER_OPTIONS];
  const marFilterDayOptions: MarFilterDayItem[] = [...MAR_FILTER_DATE];
  const [selectedMarFilters, setSelectedMarFilters] = useState<MarFilterItem[]>([...marFilterOptions]);
  const [selectedMarDay, setSelectedMarDay] = useState<MarFilterDayItem[]>([...marFilterDayOptions]);
  const [countStatRecords, setCountStatRecords] = useState(0);
  const [countScheduledRecords, setCountScheduledRecords] = useState(0);
  const [countPrnRecords, setCountPrnRecords] = useState(0);
  const [countContinuousIVRecords, setCountContinuousIVRecords] = useState(0);
  const [countInactiveOrderRecords, setCountInactiveOrderRecords] = useState(0);
  const [openAdministerSidebar, setOpenAdministerSidebar] = useState(false);
  const [administerList, setAdministerList] = useState([]);
  // SIMSNG-6033: dosage-selection sidebar for Hold/Unhold (EHR MAR only)
  const [openHoldUnholdSidebar, setOpenHoldUnholdSidebar] = useState(false);
  const [holdUnholdMode, setHoldUnholdMode] = useState(HOLD_UNHOLD_MODE.HOLD);
  const [holdUnholdRecord, setHoldUnholdRecord] = useState<MarRecord>(null);
  const isSetChartingTimeMAR = useRef(false);
  const { chartActionsProps, isAuthor, locale } = props;
  const { statSelected, scheduledSelected, prnSelected, continuousIVSelected, inactiveOrderSelected, viewDate, viewDateOptions, onSelectViewDate } = props;
  const { statRecords, scheduledRecords, prnRecords, continuousIVRecords, inactiveOrderRecords } = props;
  const { onStatChange, onScheduledChange, onPrnChange, onContinuousIVChange, onInactiveOrderChange, presaveFragment } = props;
  const {
    selectedDosageRecord,
    doseFormMode,
    fragments,
    onEditDosageRecord,
    onRecordUpdate,
    onSidebarClose,
    saveChartData,
    handleHoldMedication,
    handleUnHoldMedication,
    handleDiscontinueMedication,
    selectedNavId,
    assessment,
    setSelectedNavId,
    setChartingTimeMAR,
    isShowEHRMar,
    saveChart,
    modalService,
    chartingTimeMAR
  } = props;
  const marDosageSidebarProps: Partial<MarSidebarProps> = {
    isAuthor,
    selectedDosageRecord,
    doseFormMode,
    fragments,
    onUpdate: onRecordUpdate,
    onClose: onSidebarClose,
    saveChartData,
    viewDate,
    presaveFragment,
    selectedNavId,
    setSelectedNavId,
    assessment,
    chartingTimeMAR,
    isShowEHRMar,
    ...appHelper.getChartSharedProps(props)
  };
  const baseColumnHeaderCss = ['', 'order-start', 'order-stop', 'dose', ''];
  const dosageTimesClass = 'dosage-times';
  const actionClass = 'action';

  const onAddNewOrderClick = useCallback(() => {
    if (assessment?.eolsAssessmentId) {
      history.push(`/clinical/${assessment.eolsAssessmentId}/orders/pharmacy`);
    }
  }, [history, assessment]);

  const isShowByFilter = (filterItem: MarFilterItem) => {
    return [...selectedMarFilters].map((item) => item.toLowerCase()).includes(filterItem.toLowerCase());
  };

  const onResetFilter = () => {
    setSelectedMarFilters([...marFilterOptions]);
    setSelectedMarDay([...marFilterDayOptions]);
  };

  const renderHeaderTable = (name: string, count: number) => {
    if (!isShowEHRMar) {
      return <h2>{name}</h2>;
    }
    return (
      <h2 className="header-table">
        {name}
        {isShowEHRMar && !!count && <span>{`(${String(count).padStart(2, '0')} Orders)`}</span>}
      </h2>
    );
  };

  const getShouldShow = (filterItem: MarFilterItem, selected: boolean): boolean => {
    return isShowEHRMar ? isShowByFilter(filterItem) : selected;
  };

  const shouldShowStat = getShouldShow(STAT_LABEL, statSelected);
  const shouldShowScheduled = getShouldShow(SCHEDULED_LABEL, scheduledSelected);
  const shouldShowPrn = getShouldShow(PRN_LABEL, prnSelected);
  const shouldShowInactiveOrders = getShouldShow(INACTIVE_ORDERS_LABEL, inactiveOrderSelected);
  const shouldShowContinuousIV = getShouldShow(CONTINUOUS_IV_LABEL, continuousIVSelected);
  const allRecords = {
    statRecords: shouldShowStat ? [...statRecords] : [],
    scheduledRecords: shouldShowScheduled ? [...scheduledRecords] : [],
    prnRecords: shouldShowPrn ? [...prnRecords] : [],
    continuousIVRecords: shouldShowContinuousIV ? [...continuousIVRecords] : []
  };
  const handleOpenAdministerSidebar = (type: 'all' | 'single', record?: MarDosageRecord, item = {}) => {
    if (isShowEHRMar) {
      const allAdministerRecords = getAllAdministerRecords(allRecords, selectedMarDay, chartingTimeMAR);
      if (type === 'all') {
        setAdministerList([...allAdministerRecords]);
      }
      if (type === 'single') {
        const separateTime: MarDosageRecord[] = [];
        // Held dosages are not administrable (SIMSNG-6033), so they never enter the administer list.
        const dosageTimesFilterByDate = sortByDosageTime(record?.originDosageTimes)?.filter(
          (dosageTime) => dosageTime.dosageDate === record.dosageTimeTable && !dosageTime.isAdministered && !dosageTime.isHeld
        );
        if (dosageTimesFilterByDate?.length === 0) {
          separateTime.push({ ...record, dosageTimeSeparate: record?.orderStartTime });
        } else {
          dosageTimesFilterByDate.map((dosageTime) =>
            separateTime.push({ ...record, dosageTimeSeparate: dosageTime.dosageTime !== 'Administer' ? dosageTime.dosageTime : record?.orderStartTime })
          );
        }
        setAdministerList([...separateTime]);
      }
      setOpenAdministerSidebar(true);
      return;
    }
    onEditDosageRecord(record, item);
  };

  const checkDisabledAdminister = () => {
    const allAdministerRecords = getAllAdministerRecords(allRecords, selectedMarDay, chartingTimeMAR);
    return !allAdministerRecords.length;
  };

  const onUpdate = () => {
    setOpenAdministerSidebar(false);
    onRecordUpdate();
  };

  const handleOpenHoldSidebar = (row: MarRecord) => {
    if (!isShowEHRMar || !isScheduledOrder(row)) {
      handleHoldMedication(row);
      return;
    }
    const holdableDosageTimes = getHoldableDosageTimes(row);
    if (holdableDosageTimes.length === 0) {
      handleHoldMedication(row, [buildAdHocHeldDosage(row)]);
      return;
    }
    if (holdableDosageTimes.length === 1) {
      handleHoldMedication(row, [holdableDosageTimes[0]]);
      return;
    }
    setHoldUnholdRecord(row);
    setHoldUnholdMode(HOLD_UNHOLD_MODE.HOLD);
    setOpenHoldUnholdSidebar(true);
  };

  const handleOpenUnholdSidebar = (row: MarRecord) => {
    const heldDosageTimes = (row.dosageTimes ?? []).filter((dosageTime) => dosageTime.isHeld);
    if (!isShowEHRMar || heldDosageTimes.length === 0) {
      handleUnHoldMedication(row);
      return;
    }
    if (heldDosageTimes.length === 1) {
      const fragment = fragments.find((item) => item.fragmentId === row.fragmentId);
      saveChart([buildUnholdFragment(fragment, [heldDosageTimes[0].id], presaveFragment)], { navId: NAV_ID.PHARMACY, afterSave: onRecordUpdate });
      return;
    }
    setHoldUnholdRecord(row);
    setHoldUnholdMode(HOLD_UNHOLD_MODE.UNHOLD);
    setOpenHoldUnholdSidebar(true);
  };

  const onContinueHold = (selectedDosageTimes) => {
    setOpenHoldUnholdSidebar(false);
    handleHoldMedication(holdUnholdRecord, selectedDosageTimes);
  };

  const onHoldUnholdUpdate = () => {
    setOpenHoldUnholdSidebar(false);
    onRecordUpdate();
  };
  useEffect(() => {
    if (!chartingTimeMAR && !isSetChartingTimeMAR.current) {
      const earliestOrderStartDate = getEarliestOrderStartDate(allRecords);
      setChartingTimeMAR(earliestOrderStartDate);
      isSetChartingTimeMAR.current = !!earliestOrderStartDate;
    }
  }, [allRecords]);
  useEffect(() => {
    return () => {
      setChartingTimeMAR('');
    };
  }, []);

  return (
    <div className="mar">
      <h1>Medication Administration Record</h1>
      <Notice />

      {isShowEHRMar ? (
        <ELSFlex className="u-els-margin-top-1o2" middle spaceBetween>
          <ELSFlexItem>
            <ELSFlex className="u-els-gap-2x" middle>
              <span>Filters:</span>
              <FilterSelect
                label="Order Type"
                options={marFilterOptions}
                value={selectedMarFilters}
                isMulti
                onChange={(val) => setSelectedMarFilters(Array.from(val as MarFilterItem[]))}
              />
              <FilterSelect
                label="Dosage Day"
                options={marFilterDayOptions}
                value={selectedMarDay}
                isMulti
                onChange={(val) => setSelectedMarDay(Array.from(val as MarFilterDayItem[]))}
                showSelectAll
              />
              <ELSButton id="reset-btn" text="Reset" type="link" onClick={onResetFilter} />
            </ELSFlex>
          </ELSFlexItem>
          <ELSFlexItem>
            <ELSFlex>
              <ELSButton id="add-new-order-btn" className="u-els-margin-right-1x" text="Add New Order" type="secondary" onClick={onAddNewOrderClick} />
              <ELSButton id="administer-btn" text="Administer All" isDisabled={checkDisabledAdminister()} onClick={() => handleOpenAdministerSidebar('all')} />
            </ELSFlex>
          </ELSFlexItem>
        </ELSFlex>
      ) : (
        <ELSFlex className="u-els-margin-top-1o2">
          <ELSFlexItem>
            <ELSCheckBox labelText="Stat" checked={statSelected} changeHandler={onStatChange} />
          </ELSFlexItem>
          <ELSFlexItem className="u-els-padding-left">
            <ELSCheckBox labelText="Scheduled" checked={scheduledSelected} changeHandler={onScheduledChange} />
          </ELSFlexItem>
          <ELSFlexItem className="u-els-padding-left">
            <ELSCheckBox labelText="PRN" checked={prnSelected} changeHandler={onPrnChange} />
          </ELSFlexItem>
          <ELSFlexItem className="u-els-padding-left">
            <ELSCheckBox labelText="Continuous IV" checked={continuousIVSelected} changeHandler={onContinuousIVChange} />
          </ELSFlexItem>
          <ELSFlexItem className="u-els-padding-left">
            <ELSCheckBox labelText="Inactive Orders" checked={inactiveOrderSelected} changeHandler={onInactiveOrderChange} />
          </ELSFlexItem>
          <ELSFlexItem grow className="u-els-text-right">
            <span className="view-date-text">View Date</span>
          </ELSFlexItem>
          <ELSFlexItem>
            <ELSDropDown id="view-date-select" name="view-date" inline value={viewDate} options={viewDateOptions} changeHandler={onSelectViewDate} />
          </ELSFlexItem>
        </ELSFlex>
      )}

      {shouldShowStat && (
        <Box mt3>
          {renderHeaderTable(isShowEHRMar ? STAT_LABEL.toUpperCase() : STAT_LABEL, countStatRecords)}
          <Box mt>
            <MarTable
              viewDate={viewDate}
              hideFrequency
              hideStatus
              data={isShowEHRMar ? expandOrdersByDate(statRecords, chartingTimeMAR, false, selectedMarDay, true) : statRecords}
              onDosageClick={(record, item) => handleOpenAdministerSidebar('single', record, item)}
              handleHoldMedication={handleOpenHoldSidebar}
              handleDiscontinueMedication={handleDiscontinueMedication}
              handleUnHoldMedication={handleOpenUnholdSidebar}
              hideAdministration={false}
              locale={locale}
              isShowEHRMar={isShowEHRMar}
              chartingTime={chartingTimeMAR}
              getCount={(count) => setCountStatRecords(count)}
              modalService={modalService}
            />
          </Box>
        </Box>
      )}

      {shouldShowScheduled && (
        <Box mt3>
          {renderHeaderTable(SCHEDULED_LABEL, countScheduledRecords)}
          <Box mt>
            <MarTable
              viewDate={viewDate}
              hideStatus
              data={isShowEHRMar ? expandOrdersByDate(scheduledRecords, chartingTimeMAR, false, selectedMarDay) : scheduledRecords}
              onDosageClick={(record, item) => handleOpenAdministerSidebar('single', record, item)}
              handleHoldMedication={handleOpenHoldSidebar}
              handleDiscontinueMedication={handleDiscontinueMedication}
              handleUnHoldMedication={handleOpenUnholdSidebar}
              hideAdministration
              locale={locale}
              isShowEHRMar={isShowEHRMar}
              getCount={(count) => setCountScheduledRecords(count)}
              modalService={modalService}
              chartingTime={chartingTimeMAR}
            />
          </Box>
        </Box>
      )}

      {shouldShowPrn && (
        <Box mt3>
          {renderHeaderTable(PRN_LABEL, countPrnRecords)}
          <Box mt>
            <MarTable
              viewDate={viewDate}
              hideStatus
              data={isShowEHRMar ? expandOrdersByDate(prnRecords, chartingTimeMAR, false, selectedMarDay) : prnRecords}
              onDosageClick={(record, item) => handleOpenAdministerSidebar('single', record, item)}
              handleHoldMedication={handleOpenHoldSidebar}
              handleDiscontinueMedication={handleDiscontinueMedication}
              handleUnHoldMedication={handleOpenUnholdSidebar}
              hideAdministration={false}
              locale={locale}
              isShowEHRMar={isShowEHRMar}
              getCount={(count) => setCountPrnRecords(count)}
              modalService={modalService}
              chartingTime={chartingTimeMAR}
            />
          </Box>
        </Box>
      )}

      {shouldShowContinuousIV && (
        <Box mt3>
          {renderHeaderTable(CONTINUOUS_IV_LABEL, countContinuousIVRecords)}
          <Box mt>
            <MarTable
              viewDate={viewDate}
              hideFrequency
              hideStatus
              data={isShowEHRMar ? expandOrdersByDate(continuousIVRecords, chartingTimeMAR, false, selectedMarDay) : continuousIVRecords}
              onDosageClick={(record, item) => handleOpenAdministerSidebar('single', record, item)}
              handleHoldMedication={handleOpenHoldSidebar}
              handleDiscontinueMedication={handleDiscontinueMedication}
              handleUnHoldMedication={handleOpenUnholdSidebar}
              hideAdministration={false}
              locale={locale}
              isShowEHRMar={isShowEHRMar}
              getCount={(count) => setCountContinuousIVRecords(count)}
              modalService={modalService}
              chartingTime={chartingTimeMAR}
            />
          </Box>
        </Box>
      )}

      {shouldShowInactiveOrders && (
        <Box mt3>
          <div className="header-table__flex">
            {renderHeaderTable(INACTIVE_ORDERS_LABEL, countInactiveOrderRecords)}
            <span>
              <ELSTooltip content={<InactiveOrderTooltip />} showClose>
                <ELSIcon prefix="gizmo" name="information" color="n9" size="1x1o2" align="top" />
              </ELSTooltip>
            </span>
          </div>

          <Box mt2>
            <ELSAccordion>
              <ELSAccordionItem title="Completed, discontinued, past due, and expired medications">
                <MarTable
                  viewDate={viewDate}
                  hideFrequency
                  hideAction
                  data={inactiveOrderRecords}
                  onDosageClick={(record, item) => handleOpenAdministerSidebar('single', record, item)}
                  handleHoldMedication={handleOpenHoldSidebar}
                  handleDiscontinueMedication={handleDiscontinueMedication}
                  hideAdministration
                  locale={locale}
                  isShowEHRMar={isShowEHRMar}
                  getCount={(count) => setCountInactiveOrderRecords(count)}
                  modalService={modalService}
                  chartingTime={chartingTimeMAR}
                />
              </ELSAccordionItem>
            </ELSAccordion>
          </Box>
        </Box>
      )}

      <MarDosageSidebar {...marDosageSidebarProps} />
      <MarHoldAndDiscontinueFormSidebar {...marDosageSidebarProps} />
      <ChartPermission>{({ allowSaveFragment }) => allowSaveFragment && <ChartActions {...chartActionsProps} />}</ChartPermission>
      <MarAdministerSidebar
        administerList={administerList}
        isOpen={openAdministerSidebar}
        onCloseSidebar={() => setOpenAdministerSidebar(false)}
        fragments={fragments}
        presaveFragment={presaveFragment}
        onUpdate={onUpdate}
        saveChart={saveChart}
        isAuthor={isAuthor}
      />
      <MarHoldUnholdSidebar
        record={holdUnholdRecord}
        mode={holdUnholdMode}
        isOpen={openHoldUnholdSidebar}
        onCloseSidebar={() => setOpenHoldUnholdSidebar(false)}
        onContinueHold={onContinueHold}
        fragments={fragments}
        presaveFragment={presaveFragment}
        saveChart={saveChart}
        onUpdate={onHoldUnholdUpdate}
      />
    </div>
  );
};

MarView.displayName = 'MarView';
const mapStateToProps = (state) => ({
  selectedNavId: appSelectors.getNavId(state), // used by withChartLogic
  assessment: appSelectors.getAssessment(state),
  chartingTimeMAR: studentSelectors.getChartingTimeMAR(state)
});
const mapDispatchToProps = (dispatch) => ({
  setSelectedNavId: (navId) => dispatch(appActions.setSelectedNavId(navId)),
  setChartingTimeMAR: (dateTime) => dispatch(studentActions.setChartingTimeMAR(dateTime))
});
export default connect(mapStateToProps, mapDispatchToProps)(MarView);
